import { type Dispatch, type SetStateAction, useMemo, useState } from "react";

export const useVisibleFlags = (
  allKeys: string[],
  hiddenByDefault?: Set<string>,
  storageKey?: string
) => {
  const getDefaultColumns = () =>
    hiddenByDefault ? allKeys.filter(k => !hiddenByDefault.has(k)) : allKeys;

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            const valid = parsed.filter(k => allKeys.includes(k));
            if (valid.length > 0) {
              return valid;
            }
          }
        }
      } catch {
        // ignore parse error
      }
    }
    return getDefaultColumns();
  });

  const setAndSaveVisibleColumns: Dispatch<
    SetStateAction<string[]>
  > = updater => {
    setVisibleColumns(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (storageKey && typeof window !== "undefined") {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore storage error
        }
      }
      return next;
    });
  };

  const flags = useMemo(() => {
    return allKeys.reduce(
      (acc, key) => {
        acc[key] = visibleColumns.includes(key);
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [visibleColumns, allKeys]);

  return { visibleColumns, setVisibleColumns: setAndSaveVisibleColumns, flags };
};
